export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ORG_ADMIN: 'ORG_ADMIN',
  SITE_ENGINEER: 'SITE_ENGINEER',
  PROJECT_MANAGER: 'PROJECT_MANAGER',
  CLUSTER_HEAD: 'CLUSTER_HEAD',
  VIEWER: 'VIEWER',
} as const;

/** Human-readable labels for each canonical role key. */
export const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ORG_ADMIN: 'Organization Admin',
  SITE_ENGINEER: 'Site Engineer',
  PROJECT_MANAGER: 'Project Manager',
  CLUSTER_HEAD: 'Cluster Head',
  VIEWER: 'Viewer',
};

/**
 * Normalize a role display name or canonical identifier to a canonical role key.
 * This MUST stay in sync with apps/worker/src/middleware/auth.ts normalizeRole.
 */
export function normalizeRole(role: string): string {
  const map: Record<string, string> = {
    "admin": "ORG_ADMIN",
    "org_admin": "ORG_ADMIN",
    "org admin": "ORG_ADMIN",
    "organization admin": "ORG_ADMIN",
    "manager": "PROJECT_MANAGER",
    "project_manager": "PROJECT_MANAGER",
    "project manager": "PROJECT_MANAGER",
    "engineer": "SITE_ENGINEER",
    "site_engineer": "SITE_ENGINEER",
    "site engineer": "SITE_ENGINEER",
    "cluster head": "CLUSTER_HEAD",
    "cluster_head": "CLUSTER_HEAD",
    "viewer": "VIEWER",
    "super_admin": "SUPER_ADMIN",
    "super admin": "SUPER_ADMIN",
  };
  return map[role.toLowerCase().trim()] || role.toUpperCase().replace(/\s+/g, "_");
}

export function normalizeRoles(roles: string[]): string[] {
  return roles.map(normalizeRole);
}

export function hasNormalizedRole(roles: string[], target: string): boolean {
  const normalized = normalizeRoles(roles);
  return normalized.includes(normalizeRole(target));
}

export function hasAnyNormalizedRole(roles: string[], targets: string[]): boolean {
  const normalized = normalizeRoles(roles);
  return targets.some(t => normalized.includes(normalizeRole(t)));
}

export const hasRole = hasNormalizedRole;
export const hasAnyRole = hasAnyNormalizedRole;

/**
 * Permission keys reserved for the canonical Manager / Cluster Head approval
 * workflow. They can never be granted to custom roles, and on canonical roles
 * they are mandatory and fixed. MUST stay in sync with
 * apps/worker/src/middleware/permissions.ts PROTECTED_WORKFLOW_PERMISSIONS.
 */
export const PROTECTED_WORKFLOW_PERMISSIONS = [
  "requirement:manager_approve",
  "requirement:manager_reject",
  "requirement:cluster_approve",
  "requirement:cluster_reject",
  "actual:manager_approve",
  "actual:manager_reject",
  "actual:cluster_approve",
  "actual:cluster_reject",
] as const;

export const permissions = {
  canCreateOperationalRecord: (roles: string[]) =>
    hasNormalizedRole(roles, ROLES.SITE_ENGINEER),
  canEditOperationalRecord: (
    roles: string[],
    record: { status: string; createdBy?: string },
    userId: string | undefined
  ) =>
    hasNormalizedRole(roles, ROLES.SITE_ENGINEER) &&
    (record.status === "DRAFT" || record.status === "REJECTED_BY_MANAGER" || record.status === "REJECTED_BY_CLUSTER_HEAD") &&
    record.createdBy === userId,

  canDeleteOperationalRecord: (
    roles: string[],
    record: { status: string; createdBy?: string },
    userId: string | undefined
  ) =>
    hasNormalizedRole(roles, ROLES.SITE_ENGINEER) &&
    record.status === "DRAFT" &&
    record.createdBy === userId,

  canSubmitOperationalRecord: (
    roles: string[],
    record: { status: string; createdBy?: string },
    userId: string | undefined
  ) =>
    hasNormalizedRole(roles, ROLES.SITE_ENGINEER) &&
    record.status === "DRAFT" &&
    record.createdBy === userId,

  canManagerDecide: (roles: string[]) =>
    hasNormalizedRole(roles, ROLES.PROJECT_MANAGER),

  canClusterDecide: (roles: string[]) =>
    hasNormalizedRole(roles, ROLES.CLUSTER_HEAD),
};

/**
 * Stable signature of the caller's effective permissions, included in React
 * Query keys so cached dashboard data is isolated whenever a user's grants
 * change (login, logout, switch-user, role edit, permission edit).
 */
export function permissionSignature(permissions: string[] = []): string {
  return [...permissions].sort().join(',');
}

/**
 * Org-scope bypass for the dashboard: ORG_ADMIN (tenant admin) or a platform
 * SUPER_ADMIN acting in explicit support mode. Mirrors the worker's isOrgAdmin
 * so the web can scope admin-only rendering decisions.
 */
export function isOrgAdmin(user: { roles: string[]; scope?: 'tenant' | 'platform'; support?: unknown } | null | undefined): boolean {
  if (!user) return false;
  return hasNormalizedRole(user.roles, ROLES.ORG_ADMIN) || (user.scope === 'platform' && !!user.support);
}
